/* eslint-disable import/no-cycle */
import lodashCapitalize from 'lodash/capitalize';
import template from 'lodash/template';
import templateSettings from 'lodash/templateSettings';
import uniqWith from 'lodash/uniqWith';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import dayjs from '../../dayjs/dayjs_timezone.js';
import Translation from './localizationUtility';
import { stringToBoolean } from './conversionUtility';
import {
  parseTime as parseTime1,
  isValidDate,
  isValidISOFormat,
} from './timeUtility';
import { parseStatus } from './parser';

dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);

const interpolationRegex = /{{([\s\S]+?)}}/g;

export const prefixZero = (string, size) => {
  let s = `${string}`;
  while (s.length < size) s = `0${s}`;
  return s;
};

export const capitalize = (value) => lodashCapitalize(value);

export const parseInt = (value) => (+value ? +value : 0);

export const findProp = (props, names, options = false) => {
  if (!props) return false;

  const keys = Object.keys(props);
  for (let x = 0; x < keys.length; x++) {
    for (let y = 0; y < names.length; y++) {
      if (keys[x].toLowerCase().indexOf(names[y]) === 0 && keys[x].length === names[y].length) {
        if (!options || !options.type) {
          return props[keys[x]];
        }

        const { type } = options;
        switch (type) {
          case 'date':
            if (options.format) {
              if (isValidDate(props[keys[x]], options.format)) {
                return props[keys[x]];
              }
            }
            if (isValidISOFormat(props[keys[x]])) {
              return props[keys[x]];
            }
            break;
          case 'time':
            if (parseTime1(props[keys[x]]).isValid()) {
              return props[keys[x]];
            }
            break;
          default:
            break;
        }
      }
    }
  }

  return false;
};

const interpolateMessageToTemplate = (str, value) => {
  let message;

  templateSettings.interpolate = interpolationRegex;

  const templateFunc = template(str);

  try {
    message = templateFunc({ spaces_count: value });
  } catch {
    message = str;
  }

  return message;
};

export const formattedPrice = (value) => {
  const formatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return formatter.format(value);
};

const noDecimalPrice = (price) => price.split('.')[0];

const noDecimalWithCommaSeparatorPrice = (price) => noDecimalPrice(price).replace(/,/g, '.');

const withCommaSeparatorPrice = (price) => {
  const priceArr = price.split('.');
  priceArr[0] = priceArr[0].replace(/,/g, '.');
  return priceArr.join(',');
};

const priceInterpolationOptions = (str, value) => {
  const matches = str.match(interpolationRegex) || [];

  switch (matches[0].replace(/ /g, '')) {
    case '{{amount_no_decimals_with_comma_separator}}':
      return { amount_no_decimals_with_comma_separator: noDecimalWithCommaSeparatorPrice(value) };
    case '{{amount_with_comma_separator}}':
      return { amount_with_comma_separator: withCommaSeparatorPrice(value) };
    case '{{amount_no_decimals}}':
      return { amount_no_decimals: noDecimalPrice(value) };
    default:
      return { amount: value };
  }
};

export const interpolatePriceToTemplate = (str, value) => {
  const formattedValue = formattedPrice(value);
  let price;

  templateSettings.interpolate = interpolationRegex;

  const templateFunc = template(str);

  try {
    price = templateFunc(priceInterpolationOptions(str, formattedValue));
  } catch {
    price = formattedValue;
  }

  return price;
};

export const priceFormat = (settings) => {
  const { moneyFormats, setting } = settings;
  if (!moneyFormats) return '';
  let format;
  if (setting) format = moneyFormats[setting.price_format];
  return format || moneyFormats.moneyFormat;
};

export const defaultAvailabilityMessage = (available, translation = new Translation({})) => (
  available > 0 ? translation.key('textForHiddenRemainingPlaces') : translation.key('soldOut')
);

export const formatAvailabilityMessage = (available, total, translation = new Translation({})) => {
  let message;

  const templateTranslation = translation.key('textForShowRemainingPlaces') || '{{spaces_count}}';
  const space = translation.key('oneSpace') || 'space';
  const spaces = translation.key('multipleSpaces') || 'spaces';
  const soldOut = translation.key('soldOut') || 'Sold out';
  const only = translation.key('only') || 'Only';

  if (available <= 0) {
    message = soldOut;
  } else if (total > available) {
    message = `${only} ${available} ${available === 1 ? space : spaces}`;
  } else if (available === 1) {
    message = interpolateMessageToTemplate(templateTranslation, `${available} ${space}`);
  } else {
    message = interpolateMessageToTemplate(templateTranslation, `${available} ${spaces}`);
  }
  return message;
};

export const parseTime = (message) => {
  let output = message;
  const utcDates = output.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/g);
  if (utcDates) {
    utcDates.forEach((date) => {
      output = output.replace(date, dayjs(date).tz(window.bta.timeZone).format('LLL (z)'));
    });
  }
  return output;
};

export const findUnits = (item) => {
  const unitsNames = ['units', 'booking-party', 'booking-units'];
  const units = findProp(item.properties, unitsNames);
  if (units) {
    return ~~units;
  }

  return item.quantity;
};

export const flatten = (input, depth = 1, stack = []) => {
  for (const item of input) {
    if (item instanceof Array) {
      flatten(item, depth - 1, stack);
    } else {
      stack.push(item);
    }
  }

  return stack;
};

export const parseAvailableDates = (timeslots, options = {}) => timeslots.reduce((arr, timeslot) => {
  const { force, key } = options;
  const timeslotsKey = key || 'available_slot_count';
  if ((force && new Date(timeslot.date) >= new Date()) || timeslot[timeslotsKey] > 0) {
    arr.push(timeslot.date);
  }
  return arr;
}, []);

const selectOptionOpacity = (state) => {
  if (state.data.details?.source_urls) return 1;
  if (state.isDisabled) return '0.5';
  return state.isSelected ? '0.8' : '1';
};

const selectOptionBackgroundColor = (state, styles) => {
  const { isSelected, isFocused } = state;
  const { hover, active } = styles;
  let { backgroundColor } = styles;
  if (isSelected) {
    if (active) backgroundColor = active.backgroundColor;
  } else if (hover && isFocused) {
    backgroundColor = hover.backgroundColor;
  }
  return backgroundColor;
};

export const customSelectStyles = (styles, invalid = false, forSearch = false) => {
  if (!styles) return {};
  const { color } = styles;
  const cbc = invalid ? '#CC0000' : color;
  let controlStyles = { ...styles, boxShadow: 'unset', borderColor: `${cbc} !important` };
  if (forSearch) controlStyles = { ...controlStyles, ...{ borderRadius: '10px', minHeight: '45px' } };

  return {
    control: (provided) => ({
      ...provided,
      ...controlStyles,
    }),
    menu: (provided) => ({
      ...provided,
      ...styles,
      ...{
        zIndex: 2,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      ...{
        color: styles.color,
        opacity: selectOptionOpacity(state),
        backgroundColor: selectOptionBackgroundColor(state, styles),
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      ...styles,
    }),
    placeholder: (provided) => ({
      ...provided,
      ...{ ...styles },
    }),
  };
};

export const getStyleFromInnerWidth = (styled) => {
  const width = window.innerWidth;
  let padding = '5px';
  let { fontSize } = styled;

  if (width > 240 && width < 270) {
    padding = '3px';
    fontSize = '14px';
  } else if (width > 200 && width <= 240) {
    padding = '1px';
    fontSize = '12px';
  } else if (width <= 200) {
    padding = '0px';
    fontSize = '11px';
  }

  if (fontSize > styled.fontSize) fontSize = styled.fontSize;

  return { padding, fontSize };
};

export const getScheduleConflictMessages = (reservation, translation) => {
  let sm = '';
  let fm = '';
  const item = reservation.items.find((x) => x.status !== 'ok');

  if (item) {
    sm = capitalize(item.status).replace('_', ' ');
    fm = capitalize(parseStatus(item.status, translation));
  }

  return { fullMessage: fm, shortMessage: sm };
};

export const alphabeticallySort = (array, key) => [...array].sort((a, b) => {
  const valueA = a[key] ? a[key].trim() : '';
  const valueB = b[key] ? b[key].trim() : '';
  return valueA !== valueB ? valueA < valueB ? -1 : 1 : 0;
});

export const isDefaultTitle = (title) => ['Default Title', 'Default'].includes(title);

export const isHiddenVariant = (variants = [], variantSelected) => variants.find((v) => v.external_id === variantSelected)?.ignore;

export const isValidEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const isValidPhone = (phone) => {
  const re = /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/g;
  return re.test(String(phone).toLowerCase());
};

export const isValidPhoneNumber = (phone) => {
  const re = /^[\+]([0-9]{1,3}[\s]?[0-9]{3}|[0-9]{2,3}[\s]?[0-9]{2,3})[\s]?[0-9]{3}[\s]?[0-9]{4}$/;
  return re.test(String(phone).toLowerCase());
};

export const isValidInputDate = (date) => {
  const re = /^((\d|0[1-9]|1[0-2])[\.\/](\d|0[1-9]|1\d|2\d|3[01]){1,2}[\.\/](19|2[012])\d{2}|(19|2[012])\d{2}[\.\/](\d|0[1-9]|1\d|2\d|3[01])[\.\/](\d|0[1-9]|1[0-2]))$/;
  return re.test(date);
};

export const detectMob = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return toMatch.some((toMatchItem) => navigator.userAgent.match(toMatchItem));
};

export const detectIOS = () => {
  const toMatch = [
    /iPhone Simulator/i,
    /iPad Simulator/i,
    /iPod Simulator/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
  ];

  const uesrAgentMatch = toMatch.some((toMatchItem) => navigator.userAgent.match(toMatchItem));

  return (uesrAgentMatch || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && !window.MSStream;
};

/**
 * Get days between two dates.
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End Date.
 */
export const enumerateDaysBetweenDates = (startDate, endDate) => {
  const dates = [];
  const end = dayjs(endDate);
  let newNow = dayjs(startDate);

  while (newNow.isSameOrBefore(end)) {
    dates.push(newNow.toDate());
    newNow = newNow.add(1, 'days');
  }
  return dates;
};

export const getDepositPrice = (depositSettings, value) => {
  if (!depositSettings) return value;

  if (depositSettings.deposit_type === 'fixed') {
    value = depositSettings.amount;
  } else {
    value = parseFloat(`${value * (depositSettings.percentage / 100)}`).toFixed(2);
  }
  return value;
};

export const uniqTimeslots = (slots) => uniqWith(
  slots,
  (arrVal, othVal) => arrVal.external_id === othVal.external_id && arrVal.start === othVal.start && arrVal.finish === othVal.finish,
);

export const enableLocationDropdown = (locations, enabled) => locations && locations.length > 0 && (stringToBoolean(enabled) || isDifferentTimezones(locations));

export const isDifferentTimezones = (locations) => locations.some((location) => location.timezone !== window.bta.timeZone);

export const isScheduled = (profile) => ['activity', 'class', 'course'].includes(profile);

export const getLocation = (locations, setting, options = {}) => {  
  const { locationId, locationName, useDefault } = options;
  let location = {};
  if (locationId) location = locations.find((location) => location.id === locationId);
  if (enableLocationDropdown(locations, setting.show_location_chooser) && !location?.id) {
    const sortedLocations = alphabeticallySort(locations, 'name');
    location = sortedLocations.find(l => l.name === locationName);
    if (!location?.id && useDefault) location = sortedLocations[0];
  }
  return location;
};

export const resetErrors = (errors, key) => {
  if (!errors) return [];
  return errors.map((error) => (error.key === key ? { ...error, fieldChanged: true } : error));
};

export const getMaxTimeslotsAvailability = (timeslots) => {
  const timeslotsAvailability = [];
  timeslots.forEach((timeslot) => {
    const maxAvailable = Math.max.apply(Math, timeslot.timeslots.map((t) => t.available));
    if (maxAvailable > 0) timeslotsAvailability.push(maxAvailable);
  });
  if (!timeslotsAvailability.length) return 1;

  return Math.max(...timeslotsAvailability);
};

export const ucFirst = (str) => {
  if (!str) return str;

  return str[0].toUpperCase() + str.slice(1);
};

export const getTooltipParams = (timeslot, target) => {
  let tooltipParams = {
    messageStyles: {
      padding: '5px 5px 0 5px',
      left: target.offsetLeft,
      top: target.offsetTop,
      right: 'unset',
      minHeight: '25px',
      transform: 'translate(-30%, 15%)',
    },
  };
  if (timeslot) {
    const unavailableTimeslot = timeslot.timeslots.find((slot) => slot.status !== 1);
    const statusDetails = unavailableTimeslot?.status_detail;
    statusDetails ? tooltipParams['details'] = statusDetails : tooltipParams = null;
  } else {
    tooltipParams['details'] = { message: 'Closed', source_urls: [`${window.bta.appUrl}/admin/shop/opening_hours`] }
  }

  return tooltipParams;
};

export const renderFieldLabel = (label, markdown) => {
  const reg = new RegExp(/^[-+]?\d+[\)\.]+$/);
  return label.match(reg) ? `<p>${markdown.renderInline(label)}</p>` : markdown.render(label);
};

export const replaceSpecialSigns = (data) => (
  data.replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, '').replace(/_/g, '-')
);
